import { useEffect } from "react";
import { FiX } from "react-icons/fi";

export interface MediaLightboxProps {
  /** Media item to display */
  media: {
    type: "IMAGE" | "VIDEO" | "AUDIO" | "FILE";
    url: string;
    filename: string;
  };
  /** Close callback */
  onClose: () => void;
}

/**
 * MediaLightbox - Simple fullscreen preview for images and videos
 *
 * - Click outside or press ESC to close
 * - Images: Full-size display
 * - Videos: Playable with native controls
 */
export function MediaLightbox({ media, onClose }: MediaLightboxProps) {
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors"
        aria-label="Close preview"
      >
        <FiX className="h-6 w-6" />
      </button>

      {/* Media content */}
      <div className="max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        {media.type === "IMAGE" && (
          <img
            src={media.url}
            alt={media.filename}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
        )}

        {media.type === "VIDEO" && (
          // biome-ignore lint/a11y/useMediaCaption: User-uploaded content without captions
          <video src={media.url} controls autoPlay className="max-h-[90vh] max-w-[90vw]" />
        )}

        {/* Filename label */}
        <p className="mt-4 text-center text-sm text-white/80">{media.filename}</p>
      </div>
    </div>
  );
}

export default MediaLightbox;
